import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { GraphQL } from '@guestgoodie/graphql'
import { HugeiconsIcon } from '@hugeicons/react'
import { File01Icon } from '@hugeicons/core-solid-rounded'

import { GoodieFile, isNewFile } from '../types'
import { VideoPreview } from './VideoPreview'
import { FileButtons } from './FileButtons'

interface PreviewProps {
  allowCaptions: boolean
  value: GoodieFile
  altText?: string
  objectFit?: 'contain' | 'cover'
  readOnly?: boolean
  canReplace?: boolean
  hideControls?: boolean
  locale?: GraphQL.AppLocale
  onChangeCaptions: (captions: GraphQL.Caption[]) => void
  onChangeAltText: (altText: string) => void
  onEdit: () => void
  onRemove?: () => void
  onAlwaysMute?: () => void
}

export function Preview(props: PreviewProps) {
  const {
    allowCaptions,
    objectFit,
    value,
    altText,
    locale,
    canReplace = false,
    readOnly = false,
    hideControls = false,
    onChangeCaptions,
    onChangeAltText,
    onEdit,
    onRemove,
    onAlwaysMute
  } = props

  const [src, setSrc] = useState<string>()

  useEffect(() => {
    if (isNewFile(value)) {
      const src = URL.createObjectURL(value.file)

      setSrc(src)

      return
    }

    setSrc(value.remoteUrl)
  }, [value, setSrc])

  const type = useMemo(() => {
    const t = isNewFile(value) ? value.file.type : value.contentType

    if (t.startsWith('image')) return 'IMAGE'
    if (t.startsWith('video')) return 'VIDEO'
    return 'MISC'
  }, [value])

  const name = useMemo(
    () => (isNewFile(value) ? value?.file.name : value?.name),
    [value]
  )

  const isImage = type === 'IMAGE'
  const isVideo = type === 'VIDEO'
  const isMisc = type === 'MISC'

  return (
    <>
      {isImage && src && (
        <img
          src={src}
          className={clsx(
            'size-full',
            objectFit === 'contain' && 'object-contain',
            objectFit === 'cover' && 'object-cover'
          )}
        />
      )}
      {isVideo && src && (
        <VideoPreview
          // When the video changes we should just completely update the Preview, this way the controls/type etc. is updated correctly
          key={src}
          value={value}
          src={src}
          hideControls={hideControls}
          locale={locale}
        />
      )}
      {isMisc && (
        <div className="flex flex-col items-center justify-center gap-1">
          <HugeiconsIcon icon={File01Icon} className="size-8" />
          <span className="text-small text-text">{name}</span>
        </div>
      )}
      {!readOnly && src && (
        <FileButtons
          locale={locale}
          showCaptions={allowCaptions && isVideo}
          showReplace={canReplace}
          src={src}
          captions={value?.captions}
          onEdit={onEdit}
          onChangeCaptions={onChangeCaptions}
          onRemove={onRemove}
          showMute={isVideo}
          alwaysMute={value?.alwaysMute}
          onAlwaysMute={onAlwaysMute}
          altText={altText}
          showAltText={isImage}
          onChangeAltText={onChangeAltText}
        />
      )}
    </>
  )
}
